import './popupEdit.scss';

import React, { useEffect, useImperativeHandle, useRef, useState, useCallback, useMemo } from "react";
import { LavoriApi, WBSApi } from "../../data";
import useApiConfiguration from "../../apiConfig";
import { Button, DropDownBox, ScrollView, TreeView, Form } from "devextreme-react";
import { Popup, Position, ToolbarItem } from "devextreme-react/popup";
import { TreeViewRef } from 'devextreme-react/tree-view';
import { FormRef, Item, RequiredRule } from "devextreme-react/form";
import { useCommessaDataSource } from "../../datasources/commessaDataSource";
import { useTipoInterventoDataSource } from "../../datasources/tipoInterventoDataSource";
import { useScreenSize } from "../../utils/media-query";
import { useMezzoDataSource } from "../../datasources/mezzoDataSource";
import { ClickEvent } from "devextreme/ui/button";
import { useUserSessionContext } from "../../contexts/UserSessionContext";

export type PopupEditHandle = {
    edit: (idLavoro: number, idPersonale: number, data: Date) => void,
    new: (idPersonale: number, data: Date) => void
};

type Props = {
    onChange: () => void
}

type State = {
    idLavoro: number | undefined,
    visible: boolean,
    idPersonale: number,
    data: Date,
    firstTime: boolean
};

const PopupEdit: React.ForwardRefRenderFunction<PopupEditHandle, Props> = (props, ref) => {
    const [state, setState] = useState<State>({ idLavoro: 0, visible: false, idPersonale: 0, data: undefined, firstTime: false });
    const [formData, setFormData] = useState<any>();
    const { user } = useUserSessionContext();
    const lowerFeatures = user?.features?.map((f: string) => f.toLowerCase()) ?? [];
    const customOreViaggio = lowerFeatures.findIndex(f => f === "oreviaggioextra") > 0;

    const formRef = useRef<FormRef>(null);
    const treeRef = useRef<TreeViewRef>(null);
    const apiConfiguration = useApiConfiguration();
    const { isLarge, isSmall, isXSmall } = useScreenSize();
    const [commessaId, setCommessaId] = useState<number | null>(null);
    const [hasWBS, setHasWBS] = useState<boolean>(false);
    const [wbsDataSource, setWbsDataSource] = useState<any[]>([]);
    const [wbsFlatDataSource, setWbsFlatDataSource] = useState<any[]>([]);

    function hideInfo() {
        setCommessaId(-1);
        setWbsDataSource([]);
        setWbsFlatDataSource([]);
        setHasWBS(false);
        setState({ idLavoro: undefined, visible: false, idPersonale: 0, data: undefined, firstTime: false });
    };

    useImperativeHandle(ref, () => ({
        edit(idLavoro, idPersonale, data) {
            setState({ idLavoro: idLavoro, visible: true, idPersonale, data, firstTime: true });
        },
        new(idPersonale, data) {
            setState({ idLavoro: undefined, visible: true, idPersonale, data, firstTime: false });
        }
    }));

    useEffect(() => {
        if (state.visible) {
            if (state.idLavoro) {
                const api = new LavoriApi(apiConfiguration);
                api.getLavoroById({ idLavoro: state.idLavoro })
                    .then(function (data) {
                        if (data) {
                            setFormData({
                                ...data,
                                idLavoro: state.idLavoro
                            });

                            setCommessaId(data.idCommessa);
                        }
                    })
                    .catch();
            } else {
                setFormData({
                    idLavoro: state.idLavoro,
                    idPersonale: state.idPersonale,
                    date: state.data,
                    idWBSRamo: undefined
                });
            }
        }
    }, [state.idLavoro, state.visible]);

    const upsert = (e: ClickEvent) => {
        const validate = formRef.current?.instance().validate();

        if (validate?.isValid) {
            const api = new LavoriApi(apiConfiguration);
            const data = formRef.current?.instance().option("formData");

            if (customOreViaggio)
                data.oreViaggio = data.oreViaggioExtra;

            setFormData(data);

            api.upsertLavoro({
                lavoriInsertDTO: data
            })
                .then(() => props.onChange())
                .finally(() => hideInfo());
        }
    };

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const onFieldDataChanged = (e) => {
        if (e.dataField === "idCommessa" && e.value !== commessaId) {
            handleChange("idWBSRamo", null);
            setCommessaId(e.value || null);
        }
    };

    useEffect(() => {
        if (commessaId && commessaId > 0) {
            const api = new WBSApi(apiConfiguration);
            api.wbsByCommessa({
                idCommessa: commessaId
            }).then((res) => {
                const rami = res.wbs.rami ?? [];

                setHasWBS(rami.length !== 0)
                setWbsDataSource(rami);
                setWbsFlatDataSource(flattenTree(rami));
            }).catch(() => {
                setHasWBS(false);
                setWbsDataSource([]);
                setWbsFlatDataSource([]);
            });
        } else {
            setHasWBS(false);
            setWbsDataSource([]);
            setWbsFlatDataSource([]);
        }
    }, [commessaId]);

    function flattenTree(nodes: any[]): any[] {
        let result: any[] = [];

        nodes.forEach(node => {
            result.push(node);
            if (node.children && node.children.length > 0)
                result = result.concat(flattenTree(node.children));
        });

        return result;
    };

    return (
        <React.Fragment>
            <Popup
                visible={state.visible}
                dragEnabled={false}
                hideOnOutsideClick={true}
                showCloseButton={true}
                fullScreen={(isSmall || isXSmall)}
                showTitle={true}
                title={state.idLavoro ? "Modifica Lavoro" : "Nuovo Lavoro"}
                container=".dx-viewport"
                height={"85%"}
                onHidden={hideInfo}
            >
                <Position at="center" my="center" of="" collision="fit" />

                <ToolbarItem widget='dxButton' toolbar="bottom" location="center">
                    <Button
                        onClick={upsert}
                        icon="check"
                        text="Salva"
                        type="default"
                        stylingMode="contained"
                    />
                </ToolbarItem>

                <ScrollView>
                    <Form ref={formRef} formData={formData} labelMode="floating" colCount={isLarge ? 3 : 1} onFieldDataChanged={onFieldDataChanged}>
                        <Item visible={false} dataField="idLavoro" />
                        <Item visible={false} dataField="idPersonale" />
                        <Item visible={false} dataField="date" />

                        <Item colSpan={isLarge ? 2 : 1} dataField="idCommessa" label={{ text: "Commessa" }}
                            editorType="dxLookup" isRequired={true} disabled={!!state.idLavoro}
                            editorOptions={{
                                dataSource: useCommessaDataSource(state.idPersonale, state.data, null),
                                displayExpr: 'descrizione',
                                valueExpr: 'idCommessa',
                                searchEnabled: true
                            }}
                        />

                        <Item dataField="idTipoIntervento" label={{ text: "Tipo Intervento" }} editorType="dxSelectBox"
                            editorOptions={{
                                dataSource: useTipoInterventoDataSource(),
                                displayExpr: 'descrizione',
                                valueExpr: 'idTipoIntervento',
                                searchEnabled: true,
                                showClearButton: true
                            }}
                        />

                        <Item dataField="oreLavorate" editorType="dxNumberBox" isRequired={true}
                            editorOptions={{
                                default: 0,
                                min: 0,
                                max: 24
                            }}
                        />

                        <Item dataField="oreViaggio" label={{ text: "Di cui di vaggio" }} editorType="dxNumberBox" visible={!customOreViaggio}
                            editorOptions={{
                                default: 0,
                                min: 0,
                                max: 24
                            }}
                        />

                        <Item dataField="oreViaggioExtra" label={{ text: "Ore viaggio fuori orario lavor." }} editorType="dxNumberBox" visible={customOreViaggio}
                            editorOptions={{
                                default: 0,
                                min: 0,
                                max: 24
                            }}
                        />

                        <Item dataField="localita" label={{ text: "Località" }} editorType="dxTextBox" />

                        <Item dataField="idMezzo" label={{ text: "Mezzo" }} editorType="dxSelectBox"
                            editorOptions={{
                                dataSource: useMezzoDataSource(state.idPersonale),
                                displayExpr: 'descrizione',
                                valueExpr: 'idMezzo',
                                searchEnabled: true,
                                showClearButton: true
                            }}
                        />

                        <Item dataField="idWBSRamo" isRequired={hasWBS}>
                            {hasWBS && <RequiredRule message="Seleziona un ramo WBS" />}

                            <DropDownBox label="WBS" labelMode="floating"
                                value={formData?.idWBSRamo ?? null} dataSource={wbsFlatDataSource}
                                valueExpr="id" displayExpr="nome" disabled={!hasWBS}
                                dropDownOptions={{ height: 350 }} deferRendering={false}
                                showClearButton placeholder={"Seleziona ramo WBS..."}
                                onValueChanged={(e) => {
                                    handleChange("idWBSRamo", e.value ?? null);
                                    if (!e.value)
                                        treeRef.current?.instance().unselectAll();
                                }}
                                contentRender={() => (
                                    <TreeView className="lavori-wbs-treeview" ref={treeRef}
                                        dataSource={wbsDataSource} keyExpr="id" displayExpr="nome" dataStructure="tree"
                                        itemsExpr="children" selectionMode="single" expandNodesRecursive={true}
                                        selectNodesRecursive={false} disabledExpr="disabilitato" showCheckBoxesMode="normal"
                                        selectedItemKeys={formData?.idWBSRamo ? [formData.idWBSRamo] : []}
                                        onContentReady={(e) => {
                                            if (state.idLavoro && state.firstTime) {
                                                const tree = e.component;
                                                const selectedId = formRef.current?.instance().option("formData")?.idWBSRamo ?? null;
                                                const nodes = tree.getNodes?.() ?? [];

                                                if (!selectedId || !nodes.length) return;
                                                
                                                e.component.selectItem(selectedId);
                                            }
                                        }}
                                        onItemSelectionChanged={(e) => {
                                            if (state.firstTime) {
                                                setState(prev => ({
                                                    ...prev,
                                                    firstTime: false
                                                }));

                                                return;
                                            }

                                            const node = e.itemData;
                                            const isLeaf = !node.children || node.children.length === 0;

                                            if (!isLeaf) return;

                                            if (node.id === formData?.idWBSRamo)
                                                handleChange("idWBSRamo", null);
                                            else
                                                handleChange("idWBSRamo", node.id);
                                        }}
                                    />
                                )}
                            />
                        </Item>

                        <Item dataField="smartWorking" editorType="dxCheckBox" />

                        <Item colSpan={isLarge ? 3 : 1} dataField="nota" editorType="dxTextArea" editorOptions={{ maxLength: 250, autoResizeEnabled: true }} />
                    </Form>
                </ScrollView>
            </Popup>
        </React.Fragment>
    )
};

export default React.forwardRef(PopupEdit);